import { type IChartApi, type ISeriesApi, createChart, ColorType, LineSeries } from "lightweight-charts";
import { Panel } from "./panel-base.ts";
import { el } from "../utils/dom.ts";
import { apiGet } from "../services/api-client.ts";

interface MacroResp {
  points: Array<{ period: string; value: number }>;
}

// CPI chart: month-on-month + year-on-year % lines. Data accumulates monthly from NSO.
export class CpiChartPanel extends Panel {
  private chart: IChartApi | null = null;
  private momLine: ISeriesApi<"Line"> | null = null;
  private yoyLine: ISeriesApi<"Line"> | null = null;
  private readonly chartHost: HTMLElement;

  constructor() {
    super({ id: "cpi", title: "Lạm phát (CPI)", subtitle: "% theo tháng / theo năm" });
    this.chartHost = el("div", { className: "chart-host" });
    const legend = el("div", { className: "cpi-legend" }, [
      el("span", { className: "cpi-legend-mom" }, ["● Theo tháng (MoM)"]),
      el("span", { className: "cpi-legend-yoy" }, ["● Theo năm (YoY)"]),
    ]);
    this.setContent(legend, this.chartHost);
  }

  async load(): Promise<void> {
    try {
      const [mom, yoy] = await Promise.all([
        apiGet<MacroResp>("/api/v1/macro/series?indicator=cpi-all-mom"),
        apiGet<MacroResp>("/api/v1/macro/series?indicator=cpi-all-yoy"),
      ]);
      if (mom.points.length === 0 && yoy.points.length === 0) {
        this.setContent(el("div", { className: "panel-empty" }, ["Dữ liệu CPI đang cập nhật."]));
        return;
      }
      const chart = this.ensureChart();
      this.momLine?.setData(mom.points.map((p) => ({ time: monthTime(p.period), value: p.value })));
      this.yoyLine?.setData(yoy.points.map((p) => ({ time: monthTime(p.period), value: p.value })));
      chart.timeScale().fitContent();
    } catch {
      this.setError();
    }
  }

  private ensureChart(): IChartApi {
    if (this.chart) return this.chart;
    this.chart = createChart(this.chartHost, {
      height: 220,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#9aa4b2",
        attributionLogo: false,
      },
      grid: { vertLines: { visible: false }, horzLines: { color: "#1e2733" } },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false },
      autoSize: true,
    });
    this.momLine = this.chart.addSeries(LineSeries, { color: "#7cc4ff", lineWidth: 2 });
    this.yoyLine = this.chart.addSeries(LineSeries, { color: "#f5c518", lineWidth: 2 });
    return this.chart;
  }
}

// 'YYYY-MM' -> lightweight-charts business day (first of month).
function monthTime(period: string): never {
  return `${period}-01` as never;
}
